/**
 * "What brought you here today?" — six no-account demo experiences
 * directly below the landing hero. Real value before any signup ask.
 * Instrumented: first card clicked, step depth, completion, signup click
 * (attribution carried into trackSignUp via localStorage ml_activation).
 */
import React, { useState } from "react";
import { useCountry } from "@/lib/country";
import { Brain, Heart, MessageCircle, CalendarCheck, Moon, Map as MapIcon } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { trackCardClick } from "./activationShared";
import SampleJourney from "./experiences/SampleJourney";
import WellbeingCheck from "./experiences/WellbeingCheck";
import AskLaneDemo from "./experiences/AskLaneDemo";
import VisitPrepDemo from "./experiences/VisitPrepDemo";
import NightsDemo from "./experiences/NightsDemo";
import RoadmapExplorer from "./experiences/RoadmapExplorer";

const CARDS = [
    {
        key: "sample_journey", Icon: Brain, tone: "bg-teal-soft text-teal-dark",
        title: "I'm worried about memory changes",
        desc: "Learn where your loved one may be on the dementia journey.",
        cta: "Try a sample journey assessment", Component: SampleJourney,
        dialogTitle: "Sample Journey Assessment",
    },
    {
        key: "wellbeing_check", Icon: Heart, tone: "bg-coral-light text-coral-dark",
        title: "I'm overwhelmed",
        desc: "Caregivers often forget to care for themselves. Take a 30-second wellbeing check.",
        cta: "Take the 30-second check", Component: WellbeingCheck,
        dialogTitle: "Caregiver Wellbeing Check",
    },
    {
        key: "ask_lane", Icon: MessageCircle, tone: "bg-ml_blue-soft text-ml_blue-dark",
        title: "I need an answer",
        desc: "Ask Lane a question — see how she answers the hard ones.",
        cta: "See Lane answer a real question", Component: AskLaneDemo,
        dialogTitle: "Ask Lane — Sample Conversation",
    },
    {
        key: "visit_prep", Icon: CalendarCheck, tone: "bg-stage_early/40 text-stage_early-dark",
        title: "I'm preparing for an appointment",
        desc: "See what walking into the doctor's office prepared looks like.",
        cta: "Preview a visit prep", Component: VisitPrepDemo,
        dialogTitle: "Sample Appointment Preparation",
    },
    {
        key: "nights", Icon: Moon, tone: "bg-[#EAE6F4] text-[#5D5386]",
        title: "Nights are getting harder",
        desc: "Sundowning, sleep changes, night wandering — what helps tonight.",
        cta: "Get tonight's guide", Component: NightsDemo,
        dialogTitle: "When Nights Get Harder",
    },
    {
        key: "roadmap_explorer", Icon: MapIcon, tone: "bg-yellow-light text-yellow-dark",
        title: "I don't know what comes next",
        desc: "Explore the road ahead — from first concerns through GUIDE, home health, and hospice.",
        cta: "Explore the roadmap", Component: RoadmapExplorer,
        dialogTitle: "The Memory Lane Roadmap",
    },
];

export default function ActivationSection() {
    const { t } = useCountry();
    const [openKey, setOpenKey] = useState(null);
    const active = CARDS.find((c) => c.key === openKey);

    return (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 md:pb-20" data-testid="activation-section" data-clarity-unmask="true">
            <div className="text-center max-w-2xl mx-auto">
                <h2 className="font-heading font-semibold text-3xl md:text-4xl text-[#12776F]" data-testid="activation-heading">
                    {t("What brought you here today?")}
                </h2>
                <p className="mt-3 text-base md:text-lg text-[#4C5551]" data-testid="activation-subheading">
                    {t("Every caregiver's journey is different. Start with what's happening today — no account needed.")}
                </p>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {CARDS.map(({ key, Icon, tone, title, desc, cta }) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => { trackCardClick(key); setOpenKey(key); }}
                        data-testid={`activation-card-${key}`}
                        className="group text-left rounded-3xl bg-white/90 border border-[#E7E0CE] p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(15,76,92,0.10)] hover:border-teal/40"
                    >
                        <span className={`inline-flex w-11 h-11 rounded-2xl items-center justify-center ${tone}`}>
                            <Icon size={20} strokeWidth={1.7} />
                        </span>
                        <h3 className="mt-4 font-heading font-medium text-lg text-[#1F2624] leading-snug">{t(title)}</h3>
                        <p className="mt-1.5 text-sm text-[#4C5551] leading-relaxed">{t(desc)}</p>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-dark">
                            {t(cta)}
                            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                        </span>
                    </button>
                ))}
            </div>

            <Dialog open={Boolean(active)} onOpenChange={(o) => { if (!o) setOpenKey(null); }}>
                <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto rounded-3xl bg-cream p-6 md:p-8" data-testid="activation-dialog" aria-describedby={undefined}>
                    {active && (
                        <>
                            <DialogTitle className="font-heading font-semibold text-xl text-[#1F2624] pr-8">{t(active.dialogTitle)}</DialogTitle>
                            <div className="mt-2">
                                <active.Component onClose={() => setOpenKey(null)} />
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    );
}
